"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import { HeroSection } from "@/screens/home/elements/HeroSection";
import { createLookupObject } from "@/_helper/helper";
import { setHomeDetails } from "@/screens/home/homeSlice";
import dynamic from "next/dynamic";

const Preloader = dynamic(() =>
  import("@/components/Preloader").then((mod) => mod.Preloader),
);
const PopupBanner = dynamic<any>(() =>
  import("@/screens/home/elements/PopupBanner").then((mod) => mod.PopupBanner),
);
const BrandIntroSection = dynamic<any>(() =>
  import("@/screens/home/elements/BrandIntroSection").then(
    (mod) => mod.BrandIntroSection,
  ),
);
const CollectionSection = dynamic<any>(() =>
  import("@/screens/home/elements/CollectionSection").then(
    (mod) => mod.CollectionSection,
  ),
);
const DestinationsSection = dynamic<any>(() =>
  import("@/screens/home/elements/DestinationsSection").then(
    (mod) => mod.DestinationsSection,
  ),
);
const ExperienceSection = dynamic<any>(() =>
  import("@/screens/home/elements/ExperienceSection").then(
    (mod) => mod.ExperienceSection,
  ),
);
const FeaturedResortSection = dynamic<any>(() =>
  import("@/screens/home/elements/FeaturedResortSection").then(
    (mod) => mod.FeaturedResortSection,
  ),
);
const FinalCTASection = dynamic<any>(() =>
  import("@/screens/home/elements/FinalCTASection").then(
    (mod) => mod.FinalCTASection,
  ),
);
const SignatureExperiencesSection = dynamic<any>(() =>
  import("@/screens/home/elements/SignatureExperiencesSection").then(
    (mod) => mod.SignatureExperiencesSection,
  ),
);
const TestimonialsSection = dynamic<any>(() =>
  import("@/screens/home/elements/TestimonialsSection").then(
    (mod) => mod.TestimonialsSection,
  ),
);
const WhyKavishSection = dynamic<any>(() =>
  import("@/screens/home/elements/WhyKavishSection").then(
    (mod) => mod.WhyKavishSection,
  ),
);

interface HomeClientProps {
  initialData: any;
}

export default function HomeClient({ initialData }: HomeClientProps) {
  const dispatch = useAppDispatch();
  const homeLoading = useAppSelector((state) => state.home.loading);
  const storeHomeDetails = useAppSelector((state) => state.home.homeDetails);

  const [mounted, setMounted] = useState(false);
  const hydrated = useRef(false);

  useEffect(() => {
    setMounted(true);
    if (
      !hydrated.current &&
      initialData &&
      Object.keys(storeHomeDetails || {}).length === 0
    ) {
      dispatch(setHomeDetails(initialData));
      hydrated.current = true;
    }
  }, [dispatch, initialData, storeHomeDetails]);

  // Use initialData for the critical hydration render (when mounted is false)
  const homeDetails =
    mounted && Object.keys(storeHomeDetails || {}).length > 0
      ? storeHomeDetails
      : initialData;

  // Memoize lookup data to avoid repeated calculations
  const lookUpCMSData = useMemo(() => {
    if (homeDetails?.CMSList && homeDetails?.CMSList?.length > 0) {
      return createLookupObject(homeDetails?.CMSList, "Name");
    }
    return {};
  }, [homeDetails?.CMSList]);

  return (
    <div className="min-h-screen bg-white">
      {homeLoading && !homeDetails && <Preloader />}
      <main>
        {/* LCP fix: HeroSection moved to Server Component (page.tsx) */}
        {homeDetails?.Philosophy && homeDetails?.Philosophy?.length > 0 && (
          <BrandIntroSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        {homeDetails?.Property && homeDetails?.Property?.length > 0 && (
          <CollectionSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        {homeDetails?.Experience && homeDetails?.Experience?.length > 0 && (
          <ExperienceSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        <WhyKavishSection
          lookUpCMSData={lookUpCMSData}
          homeDetails={homeDetails}
        />
        {homeDetails?.Signature && homeDetails?.Signature?.length > 0 && (
          <SignatureExperiencesSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        {homeDetails?.SpecificProperty &&
          homeDetails?.SpecificProperty?.length > 0 && (
            <FeaturedResortSection homeDetails={homeDetails} />
          )}
        {homeDetails?.Testimonials && homeDetails?.Testimonials?.length > 0 && (
          <TestimonialsSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        {homeDetails?.Blog && homeDetails?.Blog?.length > 0 && (
          <DestinationsSection
            lookUpCMSData={lookUpCMSData}
            homeDetails={homeDetails}
          />
        )}
        <FinalCTASection
          lookUpCMSData={lookUpCMSData}
          homeDetails={homeDetails}
        />
        <PopupBanner data={homeDetails?.Popup} />
      </main>
    </div>
  );
}
